import EmbroiderRouter from '@embroider/router';
import ApplicationInstance from '@ember/application/instance';
import { type Registry as Services, service } from '@ember/service';
import { getOwner } from '@ember/application';
import { getOwnConfig, macroCondition } from '@embroider/macros';
import config from 'uplisting-frontend/config/environment';
import ApplicationController from 'uplisting-frontend/pods/application/controller';
import { routerHelper } from 'uplisting-frontend/utils/router';
import { formatLocalDate } from 'uplisting-frontend/utils/date';

const REPLACE_URLS_WITH = {
  '/connect/booking_dot_com': '/connect/booking-dot-com',
  '/connect/price_labs': '/connect/price-labs',
  '/automate/rate_rules': '/automate/rate-rules',
  '/automate/property_attributes': '/automate/property-attributes',
  '/automate/message_tags': '/automate/message-tags',
  '/automate/rental_agreements': '/automate/rental-agreements',
  '/automate/booking_windows': '/automate/booking-windows',
  '/automate/operations/management_fees':
    '/automate/operations/management-fees',
  '/automate/payment_rules': '/automate/payment-rules',
  '/automate/deposits': '/automate/identity-and-deposits',
  '/automate/identity': '/automate/identity-and-deposits',
};

const FORBIDDEN_WHEN_LAPSED_ROUTES = [
  '/dashboard',
  '/monthly',
  '/clients',
  '/insights',
  '/reports',
  '/connect',
  '/notifications',
  '/upsells',
  '/action',
  '/automate',
  '/calendar',
  '/xcalendar',
  '/direct',
  '/ai-messaging',
  '/outbound',
  '/protection',
  '/account/notifications',
  '/account/taxes',
  '/account/team',
];

export function isRouteForbiddenWhenLapsed(
  page: string | null | undefined,
): boolean {
  if (!page) {
    return false;
  }

  return FORBIDDEN_WHEN_LAPSED_ROUTES.some((route) => page.startsWith(route));
}

export default class Router extends EmbroiderRouter {
  @service router!: Services['router'];
  @service session!: Services['session'];
  @service subscription!: Services['subscription'];

  location = config.locationType;
  rootURL = config.rootURL;

  constructor(instance: ApplicationInstance) {
    super(instance);

    this.on('routeWillChange', (transition) => {
      routerHelper.activeTransition = transition;
      const toName = transition.to?.name;
      if (toName && !/(^|[._-])(loading|error)$/.test(toName)) {
        routerHelper.targetRoute = toName;
      }

      const controller = getOwner(this)?.lookup(
        'controller:application',
      ) as ApplicationController;

      controller.isLoading = true;
    });

    this.on('routeDidChange', async () => {
      routerHelper.activeTransition = undefined;

      const page = this.router.currentURL;

      if (isRouteForbiddenWhenLapsed(page)) {
        this.subscription.checkLapsed();
      }

      const replaceRouteWith = page ? REPLACE_URLS_WITH[page] : undefined;

      if (replaceRouteWith) {
        this.router.replaceWith(replaceRouteWith);
      } else if (page?.startsWith('/xaction')) {
        // the Action section used to be mounted at /xaction while Amplify
        // owned /action — keep old bookmarks and links working. The `from`
        // default has to be supplied here: the sidebar route's own
        // missing-param recovery aborts mid-redirect when the transition
        // originates from this hook.
        let target = page.replace('/xaction', '/action');

        if (!target.includes('from=')) {
          const separator = target.includes('?') ? '&' : '?';

          target = `${target}${separator}from=${formatLocalDate(new Date())}`;
        }

        this.router.replaceWith(target);
      }

      const controller = getOwner(this)?.lookup(
        'controller:application',
      ) as ApplicationController;

      controller.isLoading = false;
    });
  }
}

Router.map(function () {
  this.route('auth-callback');
  this.route('signup');
  this.route('logout');
  this.route('no-access');

  // Dev/test-only; stripped from production by the macro + build funnel.
  if (macroCondition(getOwnConfig<{ enableVrt: boolean }>().enableVrt)) {
    this.route('vrt');
  }

  this.route('comp-sets');
  this.route('protection', function () {
    this.route('overview', { path: '/' }, function () {
      this.route('bookings', { path: '/bookings/:booking_id' }, function () {
        this.route('summary', function () {
          this.route('modify');
          this.route('accept');
        });
        this.route('details');
        this.route('price', function () {
          this.route('edit');
        });
        this.route('messages');
        this.route('internal-messages');
        this.route('identity');
        this.route('logs');
        this.route('ai');
        this.route('replies', function () {
          this.route('new');
          this.route('reply', { path: '/:reply_id' });
        });
      });
    });

    this.route('disputes', function () {
      this.route('dispute', { path: '/:dispute_id' });
    });
    this.route('settings');
  });

  this.route('not-found', { path: '/*path' });
  this.route('notifications', function () {
    this.route('uplisting', function () {
      this.route('pending');
      this.route('resolved');
    });
    this.route('airbnb', function () {
      this.route('host');
      this.route('opportunities');
    });
  });
  this.route('reports', function () {
    this.route('show', { path: '/:id' });
  });
  this.route('proxy');
  this.route('compliance', function () {
    // A glob tail, so the React app's own paths stay in the address bar.
    this.route('embed', { path: '/*path' });
  });
  this.route('register', function () {
    this.route('info');
    this.route('meeting');
  });

  // default `index` route is not defined for some reason when having `this.route('dashboard');`
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  this.route('dashboard', function () {});
  this.route('clients', function () {
    this.route('statements', function () {
      this.route('deactivate');
      this.route('show', { path: '/:id' });
    });
    this.route('deactivate');
    this.route('management-fee-rules', function () {
      this.route('edit', { path: '/:id' });
    });
    this.route('settings');
    this.route('account-details');
    this.route('client', { path: '/:id' }, function () {
      this.route('profile');
      this.route('properties');
      this.route('access');
      this.route('users');
    });
  });

  this.route('monthly', function () {
    this.route('event', function () {
      this.route('unavailability');
      this.route('booking');
      this.route('closed');
      this.route('rates');
      this.route('mlos');
    });

    this.route('rates');
    this.route('mlos');
    this.route('bulk-arrival');
    this.route('bulk-departure');

    this.route('bookings', { path: '/bookings/:booking_id' }, function () {
      this.route('summary', function () {
        this.route('modify');
        this.route('accept');
      });
      this.route('details');
      this.route('price', function () {
        this.route('edit');
      });
      this.route('messages');
      this.route('internal-messages');
      this.route('identity');
      this.route('logs');
      this.route('ai');
      this.route('replies', function () {
        this.route('new');
        this.route('reply', { path: '/:reply_id' });
      });
    });

    this.route(
      'changeovers',
      { path: '/changeovers/:changeover_id' },
      function () {
        this.route('summary');
        this.route('host');
      },
    );

    this.route('enquiries', { path: '/enquiries/:enquiry_id' }, function () {
      this.route('summary');
      this.route('messages');
      this.route('ai');
      this.route('replies', function () {
        this.route('new');
        this.route('reply', { path: '/:reply_id' });
      });
    });

    this.route('unavailabilities', {
      path: '/unavailabilities/:unavailability_id',
    });

    this.route(
      'booking-requests',
      { path: '/requests/:booking_request_id' },
      function () {
        this.route('summary');
        this.route('messages');
        this.route('price');
        this.route('ai');
        this.route('replies', function () {
          this.route('new');
          this.route('reply', { path: '/:reply_id' });
        });
      },
    );
  });

  this.route('xcalendar', function () {
    this.route('reports');

    this.route('callback', function () {
      this.route('airbnb');
    });

    this.route('welcome', function () {
      this.route('airdna', function () {
        this.route('start');
      });
    });

    this.route('starts', { path: '/starts/:id' });

    this.route('bookings', { path: '/bookings/:booking_id' }, function () {
      this.route('summary', function () {
        this.route('modify');
        this.route('accept');
      });
      this.route('details');
      this.route('price', function () {
        this.route('edit');
      });
      this.route('messages');
      this.route('internal-messages');
      this.route('identity');
      this.route('logs');
      this.route('ai');
      this.route('replies', function () {
        this.route('new');
        this.route('reply', { path: '/:reply_id' });
      });
    });

    this.route(
      'booking-requests',
      { path: '/requests/:booking_request_id' },
      function () {
        this.route('messages');
        this.route('summary');
        this.route('price');
        this.route('ai');
        this.route('replies', function () {
          this.route('new');
          this.route('reply', { path: '/:reply_id' });
        });
      },
    );

    this.route('enquiries', { path: '/enquiries/:enquiry_id' }, function () {
      this.route('summary');
      this.route('messages');
      this.route('ai');
      this.route('replies', function () {
        this.route('new');
        this.route('reply', { path: '/:reply_id' });
      });
    });

    this.route(
      'changeovers',
      { path: '/changeovers/:changeover_id' },
      function () {
        this.route('summary');
        this.route('host');
      },
    );

    this.route('inbox', function () {
      this.route('bookings', { path: '/bookings/:booking_id' }, function () {
        this.route('summary', function () {
          this.route('modify');
          this.route('accept');
        });
        this.route('details');
        this.route('logs');
        this.route('ai');
        this.route('price', function () {
          this.route('edit');
        });
        this.route('messages');
        this.route('internal-messages');
        this.route('identity');
        this.route('replies', function () {
          this.route('new');
          this.route('reply', { path: '/:reply_id' });
        });
      });

      this.route(
        'booking-requests',
        { path: '/requests/:booking_request_id' },
        function () {
          this.route('summary');
          this.route('messages');
          this.route('price');
          this.route('replies', function () {
            this.route('new');
            this.route('reply', { path: '/:reply_id' });
          });
        },
      );

      this.route('enquiries', { path: '/enquiries/:enquiry_id' }, function () {
        this.route('summary');
        this.route('messages');
        this.route('replies', function () {
          this.route('new');
          this.route('reply', { path: '/:reply_id' });
        });
      });
    });

    this.route('properties', { path: '/properties/:property_id' }, function () {
      this.route('basics');
      this.route('details', function () {
        this.route('basics');
        this.route('guest-resources');
        this.route('address');
        this.route('photos');
        this.route('amenities');
        this.route('homeaway');
      });
      this.route('pricing', function () {
        this.route('base');
        this.route('discounts');
        this.route('fees');
        this.route('taxes');
      });
      this.route('channels');
      this.route(
        'automated-messages',
        { path: 'automated_messages' },
        function () {
          this.route('messages');
          this.route('message-tags', { path: 'message_tags' });
        },
      );
      this.route('attributes');
      this.route('lead-time-rate-rules', { path: 'lead_time_rate_rules' });
      this.route('mlos-lead-time-rules', { path: 'mlos_lead_time_rules' });
      this.route('tags');
      this.route('restrictions');
      this.route('links');
      this.route('remove');
      this.route('checkout');
      this.route('spot-rates', { path: 'spot_rates' });
      this.route('spot-mlos', { path: 'spot_mlos' });
      this.route('spot-arrival', { path: 'spot_arrival' });
      this.route('spot-departure', { path: 'spot_departure' });
    });

    this.route('event', { path: '/event/:property_id' }, function () {
      this.route('unavailability');
      this.route('booking');
    });

    this.route(
      'multi-units',
      { path: '/multi-units/:multi_unit_id' },
      function () {
        this.route('details', function () {
          this.route('basics');
          this.route('guest-resources');
        });
        this.route('attributes');
      },
    );

    this.route('unavailabilities', {
      path: '/unavailabilities/:unavailability_id',
    });

    this.route('rates');
    this.route('mlos');
    this.route('bulk-arrival', { path: 'bulk_arrival' });
    this.route('bulk-departure', { path: 'bulk_departure' });
  });

  this.route('calendar', function () {
    this.route('starts', { path: '/starts/:id' });

    this.route('reports');

    this.route('callback', function () {
      this.route('airbnb');
    });

    this.route('welcome', function () {
      this.route('airdna', function () {
        this.route('start');
      });
    });

    this.route('properties', { path: '/properties/:property_id' }, function () {
      this.route('basics');
      this.route('details', function () {
        this.route('basics');
        this.route('rooms');
        this.route('guest-resources');
        this.route('address');
        this.route('photos');
        this.route('amenities');
        this.route('homeaway');
      });
      this.route('pricing', function () {
        this.route('base');
        this.route('discounts');
        this.route('fees');
        this.route('taxes');
      });
      this.route('channels');
      this.route(
        'automated-messages',
        { path: 'automated_messages' },
        function () {
          this.route('messages');
          this.route('message-tags', { path: 'message_tags' });
        },
      );
      this.route('attributes');
      this.route('lead-time-rate-rules', { path: 'lead_time_rate_rules' });
      this.route('mlos-lead-time-rules', { path: 'mlos_lead_time_rules' });
      this.route('tags');
      this.route('restrictions', function () {
        this.route('minimum-stay', { path: 'minimum_stay' });
      });
      this.route('links');
      this.route('remove');
      this.route('checkout');
      this.route('spot-rates', { path: 'spot_rates' });
      this.route('spot-mlos', { path: 'spot_mlos' });
      this.route('spot-arrival', { path: 'spot_arrival' });
      this.route('spot-departure', { path: 'spot_departure' });
    });

    this.route(
      'multi-units',
      { path: '/multi-units/:multi_unit_id' },
      function () {
        this.route('details', function () {
          this.route('basics');
          this.route('guest-resources');
        });
        this.route('attributes');
      },
    );

    this.route('bookings', { path: '/bookings/:booking_id' }, function () {
      this.route('summary', function () {
        this.route('modify');
        this.route('accept');
      });
      this.route('details');
      this.route('price', function () {
        this.route('edit');
      });
      this.route('messages');
      this.route('internal-messages');
      this.route('identity');
      this.route('logs');
      this.route('ai');
      this.route('replies', function () {
        this.route('new');
        this.route('reply', { path: '/:reply_id' });
      });
    });

    this.route(
      'booking-requests',
      { path: '/requests/:booking_request_id' },
      function () {
        this.route('messages');
        this.route('summary');
        this.route('price');
        this.route('ai');
        this.route('replies', function () {
          this.route('new');
          this.route('reply', { path: '/:reply_id' });
        });
      },
    );

    this.route('enquiries', { path: '/enquiries/:enquiry_id' }, function () {
      this.route('summary');
      this.route('messages');
      this.route('ai');
      this.route('replies', function () {
        this.route('new');
        this.route('reply', { path: '/:reply_id' });
      });
    });

    this.route(
      'changeovers',
      { path: '/changeovers/:changeover_id' },
      function () {
        this.route('summary');
        this.route('host');
      },
    );

    this.route('inbox', function () {
      this.route('bookings', { path: '/bookings/:booking_id' }, function () {
        this.route('summary', function () {
          this.route('modify');
          this.route('accept');
        });
        this.route('details');
        this.route('logs');
        this.route('ai');
        this.route('price', function () {
          this.route('edit');
        });
        this.route('messages');
        this.route('internal-messages');
        this.route('identity');
        this.route('replies', function () {
          this.route('new');
          this.route('reply', { path: '/:reply_id' });
        });
      });

      this.route(
        'booking-requests',
        { path: '/requests/:booking_request_id' },
        function () {
          this.route('summary');
          this.route('messages');
          this.route('price');
          this.route('replies', function () {
            this.route('new');
            this.route('reply', { path: '/:reply_id' });
          });
        },
      );

      this.route('enquiries', { path: '/enquiries/:enquiry_id' }, function () {
        this.route('summary');
        this.route('messages');
        this.route('replies', function () {
          this.route('new');
          this.route('reply', { path: '/:reply_id' });
        });
      });
    });

    this.route('event', { path: '/event/:property_id' }, function () {
      this.route('unavailability');
      this.route('booking');
      this.route('closed');
      this.route('rates');
      this.route('mlos');
    });

    this.route('unavailabilities', {
      path: '/unavailabilities/:unavailability_id',
    });

    this.route('rates');
    this.route('mlos');
    this.route('bulk-arrival');
    this.route('bulk-departure');
  });

  this.route('action-assignment-rules', {
    path: '/action/assignment-rules',
  });

  this.route('action-changeover-settings', {
    path: '/action/changeover-settings',
  });

  this.route('action', function () {
    this.route('bookings', { path: '/bookings/:booking_id' }, function () {
      this.route('summary', function () {
        this.route('modify');
        this.route('accept');
      });
      this.route('details');
      this.route('price', function () {
        this.route('edit');
      });
      this.route('messages');
      this.route('internal-messages');
      this.route('identity');
      this.route('logs');
      this.route('ai');
      this.route('replies', function () {
        this.route('new');
        this.route('reply', { path: '/:reply_id' });
      });
    });

    this.route(
      'changeovers',
      { path: '/changeovers/:changeover_id' },
      function () {
        this.route('summary');
        this.route('host');
      },
    );
  });

  this.route('automate', function () {
    this.route('all');
    this.route('markup');
    this.route('payment-rules');
    this.route('payments');
    this.route('message-tags');
    this.route('property-attributes');
    this.route('enquiries');
    this.route('reviews');
    this.route('cleaning');
    this.route('pricing-sync', { path: 'pricing_sync' });
    this.route('rate-rules');
    this.route('exchange-rates');
    this.route('booking-windows');
    this.route('identity-and-deposits');
    this.route('rental-agreements');
    this.route('guest-booking-page');
    this.route('operations', function () {
      this.route('management-fees');
    });
    this.route('new-listing-promotions');
    this.route('ai', function () {
      this.route('knowledge-center');
      this.route('content-files');
      this.route('sandbox');
      this.route('controls');
    });
  });

  this.route('direct', function () {
    this.route('link');
    this.route('template');
    this.route('content');
    this.route('design');
    this.route('listings');
    this.route('terms');
    this.route('promotions');
    this.route('plan');
  });
  this.route('ai-messaging', function () {
    this.route('knowledge-center');
    this.route('content-files');
  });
  this.route('outbound', function () {
    this.route('messages', function () {
      this.route('new');
      this.route('edit', { path: '/edit/:id' });
    });
  });
  this.route('insights', function () {
    this.route('all');
    this.route('settings');
    this.route('airbnb-quality-dashboard');

    this.route('reports', function () {
      this.route('show', { path: '/:id' });
    });
  });

  this.route('account', function () {
    this.route('team', function () {
      this.route('roles');
      this.route('person', { path: '/:id' }, function () {
        this.route('details');
        this.route('properties');
        this.route('permissions');
      });
    });
    this.route('settings');
    this.route('notifications');
    this.route('billing');
    this.route('subscribing');
    this.route('taxes');
    this.route('lapsed');
    this.route('verify');
  });

  this.route('connect', function () {
    this.route('callback', function () {
      this.route('airbnb');
      this.route('edgestate');
      this.route('stripe');
      this.route('official');
    });

    this.route('wheelhouse');
    this.route('seam', { path: 'smart-locks' });
    this.route('airbnb');
    this.route('edgestate');
    this.route('stripe');
    this.route('zapier');
    this.route('webhook');
    this.route('all');
    this.route('booking-dot-com');
    this.route('homeaway');
    this.route('price-labs');
    this.route('adapt');
    this.route('beyond');
    this.route('icals');
    this.route('api');
    this.route('safely');
    this.route('guest-id', { path: 'guestid' });
    this.route('turnoverbnb');
    this.route('minut');
    this.route('protect');
    this.route('pricing');
    this.route('besty');
    this.route('stay-fi');
    this.route('direct', function () {
      this.route('google-analytics');

      this.route('facebook-pixel');
      this.route('intercom');
    });

    this.route('listings', function () {
      this.route('mapping', function () {
        this.route('callback');
      });
      this.route('publish');
    });
  });

  this.route('upsells', function () {
    this.route('edit', { path: '/:id' });
    this.route('new');
    this.route('orders');
  });
});
